/**
 * What the system check found before its first signature (E1, E2, E3); E3 warns when the register
 * names another company.
 */
import { Modal, cn } from '@insio/ui-toolkit';
import { useFeatureTranslation } from '@/hooks/useFeatureTranslation';
import { useFormatShortDate } from '@/hooks/use-date-formatters';
import { SystemCheckRule } from '@insio/shared-types';
import { isRegisterNameDifferent, type SystemCheckEvidence } from '../utils/system-check-evidence';

/** The registers E3 asks, by the code the evidence records. */
const REGISTER_KEYS: Readonly<Record<string, string>> = {
  ADIS: 'registers.ADIS',
  ODFS: 'registers.ODFS'
};

interface SystemCheckEvidenceDetailsProps {
  evidence: SystemCheckEvidence;
  /** The partner's name now, compared with the name the register gives. */
  partyName: string | null;
}

interface EvidenceFact {
  label: string;
  value: string | null;
  isIdentifier: boolean;
}

export function SystemCheckEvidenceDetails({
  evidence,
  partyName
}: SystemCheckEvidenceDetailsProps) {
  const t = useFeatureTranslation('partyBankAccounts.evidence');
  const formatShortDate = useFormatShortDate();

  if (evidence.rule === SystemCheckRule.E1) {
    return (
      <p className='text-sm text-foreground-base' data-testid='bank-account-evidence-e1'>
        {evidence.connectorName ? t('e1Named', { connector: evidence.connectorName }) : t('e1')}
      </p>
    );
  }

  if (evidence.rule === SystemCheckRule.E2) {
    return (
      <p className='text-sm text-foreground-base' data-testid='bank-account-evidence-e2'>
        {t('e2')}
      </p>
    );
  }

  const nameDiffers = isRegisterNameDifferent(evidence.registerName, partyName);
  const registerKey = evidence.register ? REGISTER_KEYS[evidence.register] : undefined;
  // Identifiers line up in SF Mono; names and dates are text. The SK register's IČO only when given.
  const facts: EvidenceFact[] = [
    {
      label: t('register'),
      value: registerKey ? t(registerKey) : evidence.register,
      isIdentifier: false
    },
    { label: t('vatId'), value: evidence.vatId, isIdentifier: true },
    { label: t('companyId'), value: evidence.companyId, isIdentifier: true },
    ...(evidence.registerCompanyId
      ? [{ label: t('registerCompanyId'), value: evidence.registerCompanyId, isIdentifier: true }]
      : []),
    { label: t('registerName'), value: evidence.registerName, isIdentifier: false },
    {
      label: t('checkedOn'),
      value: evidence.checkedOn ? formatShortDate(evidence.checkedOn) : null,
      isIdentifier: false
    }
  ];

  return (
    <div className='flex flex-col gap-sm' data-testid='bank-account-evidence-e3'>
      <p className='text-sm text-foreground-base'>{t('e3')}</p>
      <dl className='grid grid-cols-[auto_1fr] gap-x-md gap-y-2xs text-sm'>
        {facts.map(({ label, value, isIdentifier }) => (
          <div key={label} className='contents'>
            <dt className='text-foreground-muted'>{label}</dt>
            <dd
              className={cn(
                value ? 'text-foreground-base' : 'text-foreground-muted',
                value && isIdentifier && 'font-mono tabular-nums'
              )}
            >
              {value ?? t('notGiven')}
            </dd>
          </div>
        ))}
      </dl>
      {nameDiffers && (
        <Modal.Warning testId='bank-account-register-name-mismatch'>
          {t('nameMismatch', { partyName: partyName ?? '' })}
        </Modal.Warning>
      )}
    </div>
  );
}
